<template>
  <div
    class="IsoCheckAnimation"
  >
    <IsoChatOverlay
      ref="overlayRef"
      :vector="overlayVector"
    >
      <IsoChatGroup>
        <IsoChat
          ref="userChatRef"
          type="User"
          :fragments="['Can I afford a $2,500', 'vacation in August?']"
        />
        <IsoChat
          ref="systemChatRef"
          type="System"
          :fragments="['Yes—I’ve created', 'a routine to', 'set aside 10%', 'of your paycheck.']"
        />
      </IsoChatGroup>
    </IsoChatOverlay>
  </div>
</template>

<script lang="ts" setup>
import useCamera from "./useCamera";
import useDisposer from "./useDisposer";
import usePan from "./usePan";
import useThreeElement from "./useThreeElement";
import delay from "./delay";
import { Group, Object3D, Vector3 } from "three";
import { onMounted, ref, toRefs } from "vue";
import IsoChat from "./IsoChat.vue";
import IsoChatGroup from "./IsoChatGroup.vue";
import IsoChatOverlay from "./IsoChatOverlay.vue";
import IsoCheckmark from "./IsoCheckmark";
import IsoPromptBlock from "./IsoPromptBlock";
import Colors from "@cosine/lib/Colors";

const props = defineProps<{
  parent: Object3D,
}>();

defineExpose({
  animate,
});

const {
  canvas,
} = useThreeElement();

const {
  tileWidth,
  animateCameraZoom,
} = useCamera();

const {
  animatePan,
} = usePan();

const {
  parent,
} = toRefs(props);

const group = new Group();
parent.value.add(group);

const overlayRef = ref<InstanceType<typeof IsoChatOverlay>>();
const userChatRef = ref<InstanceType<typeof IsoChat>>();
const systemChatRef = ref<InstanceType<typeof IsoChat>>();
const overlayVector = new Vector3(0, 0, 0);

const {
  autoDispose,
} = useDisposer();
let block: IsoPromptBlock;

onMounted(() => {
  block = autoDispose(new IsoPromptBlock(group));
  block.meshGroup.position.y = -1;
  block.meshGroup.visible = false;
});

async function animate () {
  await animateBlockChat();
  await animateCheckmark();
}

async function animateBlockChat () {
  block.meshGroup.visible = true;
  await block.animateIn(canvas.requestRender);

  overlayRef.value?.animateIn();
  await delay(700);
  await userChatRef.value?.animateIn();
  await block.animateThinking(canvas.requestRender);
  await block.animateAnswering(({
    index,
  }) => {
    systemChatRef.value?.animateIn({
      index,
    });
  }, canvas.requestRender);

  await delay(1000);

  userChatRef.value?.animateOut();
  systemChatRef.value?.animateOut();
  await delay(650);
  await overlayRef.value?.animateOut();
  await block.animateDown(canvas.requestRender);
}

async function animateCheckmark (): Promise<void> {
  const checkmark = autoDispose(new IsoCheckmark(group, {
    color: Colors.purple300,
  }));
  const minTileCount = Math.floor((canvas.width / tileWidth.value) - 1);
  const checkTileWidth = canvas.width > 600 && canvas.width > canvas.height ? 12 : 7;
  const maxZoom = 1;
  const zoom = Math.min(maxZoom, minTileCount / checkTileWidth);
  const horOffset = checkTileWidth / 2 * (1 - zoom);

  block.meshGroup.visible = false;

  await Promise.all([
    checkmark.animateIn(canvas.requestRender),
    animateCameraZoom({
      zoom,
      duration: 1000,
    }),
    animatePan({
      position: new Vector3(-horOffset, 0, horOffset),
      duration: 1000,
    }),
  ]);

  await Promise.all([
    checkmark.animateOut(canvas.requestRender),
    animateCameraZoom({
      zoom: 1,
      duration: 1500,
    }),
    animatePan({
      position: new Vector3(-5, 0, 10),
      duration: 1500,
    }),
  ]);

  checkmark.group.visible = false;
}
</script>
